import React, { useState } from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard,
  AccountBalance,
  People,
  ShoppingCart,
  LocalHospital,
  Receipt,
  Business,
  ContactSupport,
  Analytics,
  Security,
  AccountTree,
  Notifications,
  AccountCircle,
  Language,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

const drawerWidth = 280;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { text: t("common.dashboard"), icon: <Dashboard />, path: "/dashboard" },
    { text: t("common.finance"), icon: <AccountBalance />, path: "/finance" },
    { text: t("common.hr"), icon: <People />, path: "/hr" },
    {
      text: t("common.procurement"),
      icon: <ShoppingCart />,
      path: "/procurement",
    },
    { text: t("common.clinical"), icon: <LocalHospital />, path: "/clinical" },
    { text: t("common.billing"), icon: <Receipt />, path: "/billing" },
    { text: t("common.facilities"), icon: <Business />, path: "/facilities" },
    { text: t("common.crm"), icon: <ContactSupport />, path: "/crm" },
    { text: t("common.analytics"), icon: <Analytics />, path: "/analytics" },
    { text: t("common.compliance"), icon: <Security />, path: "/compliance" },
    {
      text: t("common.multiEntity"),
      icon: <AccountTree />,
      path: "/multi-entity",
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (newLanguage: string) => {
    i18n.changeLanguage(newLanguage);
    handleMenuClose();
  };

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Toolbar sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "flex-start",
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/Logo.jpeg`}
            alt="Elaj Group Logo"
            sx={{
              height: 60,
              width: 60,
              mr: 1,
              objectFit: "contain",
              borderRadius: 1,
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ fontWeight: 600 }}
            >
              Elaj Group ERP
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ flex: 1, py: 1 }}>
        <List sx={{ px: 1 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  py: 1.5,
                  "&.Mui-selected": {
                    background:
                      "linear-gradient(135deg, #2E7D8A 0%, #5BA3B0 100%)",
                    color: "white",
                    boxShadow: "0px 4px 12px rgba(46, 125, 138, 0.3)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #1A5A66 0%, #2E7D8A 100%)",
                    },
                    "& .MuiListItemIcon-root": {
                      color: "white",
                    },
                    "& .MuiListItemText-primary": {
                      fontWeight: 500,
                    },
                  },
                  "&:hover": {
                    background: "rgba(46, 125, 138, 0.08)",
                    transform: "translateX(4px)",
                    transition: "all 0.2s ease-in-out",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: location.pathname === item.path ? 500 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          display="block"
        >
          Version 1.0.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        maxWidth: "100vw",
        overflow: "hidden",
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background: "linear-gradient(135deg, #2E7D8A 0%, #5BA3B0 100%)",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Toolbar sx={{ px: 3 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              display: { md: "none" },
              "&:hover": {
                background: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ fontWeight: 600 }}
            >
              {menuItems.find((item) => item.path === location.pathname)
                ?.text || "Dashboard"}
            </Typography>
            <Typography
              variant="caption"
              color="rgba(255, 255, 255, 0.8)"
              sx={{ fontSize: "0.75rem" }}
            >
              {new Date().toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              color="inherit"
              sx={{
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <Notifications />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              sx={{
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              <MenuItem
                onClick={() => handleLanguageChange("en")}
                sx={{ py: 1.5 }}
              >
                <Language sx={{ mr: 1.5, color: "primary.main" }} />
                English
              </MenuItem>
              <MenuItem
                onClick={() => handleLanguageChange("ar")}
                sx={{ py: 1.5 }}
              >
                <Language sx={{ mr: 1.5, color: "primary.main" }} />
                العربية
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, sm: 8 },
          background: "linear-gradient(135deg, #FAFBFC 0%, #F8F9FA 100%)",
          minHeight: "calc(100vh - 64px)",
          overflow: "auto", // Allow scrolling if needed
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%", // Ensure content doesn't exceed container
            boxSizing: "border-box",
            "& > *": {
              animation: "fadeInUp 0.6s ease-out",
            },
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(20px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
